import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import { useMatchMedia } from '@/hooks/useMatchMedia/useMatchMedia';

import type { ScrollDirection } from '@/hooks/useScroll/useScroll';

// ── Scroll Context ──────────────────────────────────────────────

interface ScrollContextValue {
  isScrolled: boolean;
  scrollDirection: ScrollDirection;
}

const ScrollContext = createContext<ScrollContextValue>({ isScrolled: false, scrollDirection: null });

/** 스크롤 상태 구독 (내부 전용 — HeaderBottom에서 사용) */
export const useScrollContext = (): ScrollContextValue => useContext(ScrollContext);

/** 스크롤 Provider */
export const ScrollProvider = ({ value, children }: { value: ScrollContextValue; children: ReactNode }) => (
  <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>
);

// ── Action Context ──────────────────────────────────────────────

interface ActionContextValue {
  onAction?: (action: string, meta: Record<string, unknown>, originalEvent: React.MouseEvent) => void;
}

const ActionContext = createContext<ActionContextValue>({});

/** 모든 Action/Logo가 사용 */
export const useActionContext = (): ActionContextValue => useContext(ActionContext);

/** onAction 핸들러 Provider. useCallback으로 안정화되어 거의 변경 안 됨. */
export const ActionProvider = ({ value, children }: { value: ActionContextValue; children: ReactNode }) => (
  <ActionContext.Provider value={value}>{children}</ActionContext.Provider>
);

// ── Breakpoint Context ──────────────────────────────────────────

const BreakpointContext = createContext<boolean>(false);

/**
 * 현재 뷰포트가 데스크탑인지 여부.
 *
 * `<Header>` 컴포넌트 내부에서만 동작합니다. Header가 내부적으로 사용하는 것과
 * 동일한 `matchMedia` 구독을 공유하므로, 첫 렌더부터 정확한 값을 반환하여
 * `onBreakpointChange + useState` 패턴에서 발생하는 mobile→desktop first-paint
 * flash가 발생하지 않습니다.
 *
 * `<Header>` 외부에서 호출하면 기본값 `false`를 반환합니다 (페이지 루트 레벨의
 * 분기에는 `<Header onBreakpointChange={...}>` 콜백을 사용하세요).
 *
 * @example
 * function MyHeaderArea() {
 *   const isDesktop = useIsDesktop();
 *   return (
 *     <Header.Bottom behavior={isDesktop ? 'collapse' : 'reveal'}>
 *       <LNB />
 *     </Header.Bottom>
 *   );
 * }
 *
 * **임계값은 `BREAKPOINT_LAPTOP`(1024) 이다.** 정본(nol-design-tokens)은 그 폭을 `laptop`
 * 이라 부르고, core-ui 는 그 폭부터를 *데스크탑 레이아웃* 이라 부른다 — 이름이 다른 두 층이다.
 * 자세히는 `styles/_breakpoints.ts` 참조.
 */
export const useIsDesktop = (): boolean => useContext(BreakpointContext);

/**
 * 브레이크포인트 Provider.
 * matchMedia로 isDesktop 판단 → data-core-ui-breakpoint 속성 제어.
 * visibility·레이아웃 모두 data-core-ui-breakpoint 속성 기반 CSS로 동작.
 */
export const BreakpointProvider = ({
  value,
  onChange,
  children,
}: {
  value: number;
  onChange?: (isDesktop: boolean) => void;
  children: ReactNode;
}) => {
  const isDesktop = useMatchMedia(`(min-width: ${value}px)`, onChange);

  return <BreakpointContext.Provider value={isDesktop}>{children}</BreakpointContext.Provider>;
};
